import React, { useState } from 'react';
import { Card } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import InsightPage, {
  StatRow, StatCard, CopyStats, ControlSelect, useChartTheme, maxBy,
  PHONE_CHART_HEIGHT, PHONE_CHART_MARGIN, PHONE_X_AXIS, PHONE_Y_AXIS, shortDay,
} from '../../components/insights/InsightPage';
import DateWindowSelect from '../../components/insights/DateWindowSelect';
import { insightsService, officeService } from '../../services/apiService';
import { useInsightData } from '../../hooks/useInsightData';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { EMPTY, formatDate, formatNumber, plural } from '../../utils/format';

/**
 * Monday to Friday dates from `days` ago to today, the same window the backend counts from. The
 * backend only counts weekday bookings, so the average is per weekday rather than per calendar day.
 */
const weekdaysInWindow = (days) => {
  let count = 0;
  const date = new Date();
  for (let i = 0; i <= days; i += 1) {
    const dow = date.getDay();
    if (dow !== 0 && dow !== 6) count += 1;
    date.setDate(date.getDate() - 1);
  }
  return count;
};

const ParkingWeekdays = () => {
  const isMobile = useIsMobile();
  const t = useChartTheme();
  const [days, setDays] = useState(90);
  const [officeId, setOfficeId] = useState('');

  const { data: offices } = useInsightData(() => officeService.getAllOffices(), []);
  const { data, loading, error, reload } = useInsightData(
    () => insightsService.parkingByWeekday(days, officeId || null),
    [days, officeId]
  );
  const rows = data?.byWeekday || [];
  const parkingEnabledOffices = data?.parkingEnabledOffices ?? 0;

  const total = rows.reduce((s, r) => s + r.bookings, 0);
  const busiest = total > 0 ? maxBy(rows, 'bookings') : null;
  const weekdays = weekdaysInWindow(days);
  // The backend counts bookings dated from this day on, so bookings made ahead for future days count too
  const since = new Date();
  since.setDate(since.getDate() - days);

  // The office count covers every office with parking, so the "across" line only fits the unfiltered view
  const copy = busiest
    ? (officeId
      ? `${busiest.day} is the busiest day in this car park, with ${plural(busiest.bookings, 'booking')} since ${formatDate(since)}. 🚗`
      : `Across ${plural(parkingEnabledOffices, 'office')} with parking, the car park is busiest on ${busiest.day}, with ${plural(busiest.bookings, 'booking')} since ${formatDate(since)}. 🚗`)
    : '';

  // Only offices that actually have parking enabled (capacity > 0), in name order. The office list
  // carries no company names, so offices that share a name cannot be told apart here yet.
  const officeList = (offices || [])
    .filter((o) => (o.carParkCapacity || 0) > 0)
    .sort((a, b) => (a.label || '').localeCompare(b.label || '', 'en-GB', { sensitivity: 'base' }));

  const controls = (
    <>
      <DateWindowSelect value={days} onChange={setDays} />
      <ControlSelect id="parkingOffice" label="Office" value={officeId} onChange={(e) => setOfficeId(e.target.value)} style={{ maxWidth: isMobile ? undefined : 260 }}>
        <option value="">All offices</option>
        {officeList.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </ControlSelect>
    </>
  );

  return (
    <InsightPage
      title="Parking pressure"
      description="When the car park is busiest, across offices with parking enabled."
      controls={controls}
      loading={loading}
      error={error}
      onRetry={reload}
      ready={data != null}
      empty={total === 0}
      emptyMessage={officeId ? 'No parking booked at this office in this window' : 'No parking booked in this window'}
    >
      <StatRow>
        <StatCard label="Busiest day" value={busiest?.day || EMPTY} sub={busiest ? plural(busiest.bookings, 'booking') : ''} variant="primary" />
        <StatCard label="Total bookings" value={formatNumber(total)} />
        <StatCard label="Average a weekday" value={formatNumber(weekdays ? Math.round(total / weekdays) : 0)} sub={`Over the ${plural(weekdays, 'weekday')} since ${formatDate(since)}`} />
        <StatCard
          label="Offices with parking"
          value={formatNumber(parkingEnabledOffices)}
          sub={officeId ? 'Figures above are for one of them' : 'Car park capacity set'}
        />
      </StatRow>

      <Card className="shadow-sm">
        <Card.Header className="bg-light"><h5 className="mb-0">Parking bookings by weekday</h5></Card.Header>
        <Card.Body>
          <ResponsiveContainer width="100%" height={isMobile ? PHONE_CHART_HEIGHT : 400}>
            <BarChart data={rows} margin={isMobile ? PHONE_CHART_MARGIN : { top: 10, right: 30, left: 10, bottom: 20 }}>
              <CartesianGrid {...t.grid} />
              <XAxis dataKey="day" {...t.xAxis} {...(isMobile ? { ...PHONE_X_AXIS, tickFormatter: shortDay } : {})} />
              <YAxis allowDecimals={false} {...t.yAxis} {...(isMobile ? PHONE_Y_AXIS : {})} />
              <Tooltip {...t.tooltip} formatter={(v) => formatNumber(v)} />
              <Bar dataKey="bookings" name="Bookings">
                {rows.map((r) => (
                  <Cell key={r.day} fill={r === busiest ? t.colors.emphasis : t.colors.neutral} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>

      {copy && <CopyStats text={copy} />}
    </InsightPage>
  );
};

export default ParkingWeekdays;
